import moment from 'moment';
import {
  PARTNER_AGREEMENT_CHANGE_VALUES,
} from '@shared/store/constants/mutationTypes';

import apiClient from '@shared/lib/general/apiClient';
import initialState from './partnerAgreementInitialState';

const kitSkus = [{
  label: 'SMBP1G1A1',
  value: 'SMBP1G1A1',
}, {
  label: 'SMBP2G1A2',
  value: 'SMBP2G1A2',
}];
const sensorSkus = [{
  label: 'SMCSG01A1',
  value: 'SMCSG01A1',
}, {
  label: 'SMFSG01A1',
  value: 'SMFSG01A1',
}, {
  label: 'SMWSG01A1',
  value: 'SMWSG01A1',
}, {
  label: 'SMCSG01A2',
  value: 'SMCSG01A2',
}, {
  label: 'SMFSG01A2',
  value: 'SMFSG01A2',
}, {
  label: 'SMWSG01A2',
  value: 'SMWSG01A2',
}];

const getPaymentMethods = async (corpId) => {
  const { data: allPaymentMethods } = await apiClient({
    method: 'GET',
    url: `/chargebee/corporation/${corpId}/payment-method/all`,
  });
  const { data: chargebeeCustomer } = await apiClient({
    method: 'GET',
    url: `/chargebee/corp/${corpId}/customer`,
  });
  const defaultPaymentMethod = allPaymentMethods.find((x) => (
    x.id === chargebeeCustomer.primary_payment_source_id
  ));
  return {
    corpId,
    chargebeeCustomer,
    defaultPaymentMethod,
    allPaymentMethods,
  };
};

const getTaxExemptStatus = async (corpId) => {
  const { data } = await apiClient({
    method: 'GET',
    url: `/partner/settings/${corpId}`,
  });
  const {
    TAX_EXEMPT,
    TAX_EXEMPT_DOCUMENT,
    TAX_EXEMPT_REASON,
    TAX_EXEMPT_NUMBER,
    TAX_EXEMPT_EXPIRATION,
  } = data;
  return {
    corpId,
    exempt: TAX_EXEMPT === 'true',
    documentUrl: TAX_EXEMPT_DOCUMENT,
    reason: TAX_EXEMPT_REASON,
    number: TAX_EXEMPT_NUMBER,
    expiration: TAX_EXEMPT_EXPIRATION,
  };
};

const getCorporationInfo = async (corpId) => {
  const { data } = await apiClient({
    method: 'GET',
    url: `/corporations/id/${corpId}`,
  });
  return data;
};

const actions = {
  changePartnerAgreementValues({ commit }, payload) {
    commit({
      type: PARTNER_AGREEMENT_CHANGE_VALUES,
      changes: payload,
    });
  },
  async getPartnerAgreement({ dispatch }, agreementId) {
    const { data: agreement } = await apiClient({
      method: 'GET',
      url: `/partner/agreement/${agreementId}`,
    });

    const { data: branches } = await apiClient({
      method: 'GET',
      url: `/corporations/id/${agreement.corpId}/branches`,
    });

    const { data: psm } = await apiClient({
      method: 'GET',
      url: `/corporations/id/${agreement.corpId}/psm`,
    });

    const { data: partnerSettings } = await apiClient({
      method: 'GET',
      url: `/partner/settings/${agreement.corpId}`,
    });

    dispatch('changePartnerAgreementValues', [{
      field: 'agreement',
      value: agreement,
    }, {
      field: 'partnerBranches',
      value: branches,
    }, {
      field: 'psm',
      value: psm,
    }, {
      field: 'partnerSettings',
      value: partnerSettings,
    }]);
  },
  async getAgreementPaymentMethods({ dispatch, state }) {
    const {
      payee,
      agreement: {
        agreementOrders,
      },
    } = state;

    const allCorpIds = [];
    Object.values(payee).forEach((id) => {
      if (!allCorpIds.includes(id)) {
        allCorpIds.push(id);
      }
    });

    const infoReqs = allCorpIds.map((id) => getCorporationInfo(id));
    const paymentMethodReqs = allCorpIds.map((id) => getPaymentMethods(id));
    const taxExemptReqs = allCorpIds.map((id) => getTaxExemptStatus(id));

    const infoRes = await Promise.all(infoReqs);
    const res = await Promise.all(paymentMethodReqs);
    const taxRes = await Promise.all(taxExemptReqs);

    const companyInfos = {};
    const paymentMethods = {};
    const taxExemption = {};
    const chargebeeCustomers = {};

    const selectedPaymentMethods = {};

    infoRes.forEach((info) => {
      const {
        id,
      } = info;
      companyInfos[id] = info;
    });

    res.forEach((pm) => {
      const {
        corpId,
        chargebeeCustomer,
        defaultPaymentMethod,
        allPaymentMethods,
      } = pm;
      paymentMethods[corpId] = {
        defaultPaymentMethod,
        allPaymentMethods,
      };
      chargebeeCustomers[corpId] = chargebeeCustomer;
      let selectedPaymentMethodId = null;
      if (agreementOrders) {
        agreementOrders.forEach((order) => {
          const {
            depositCorpId,
            depositMethodId,
            subscriptionCorpId,
            subscriptionMethodId,
          } = order;
          if (depositCorpId === corpId && depositMethodId) {
            selectedPaymentMethodId = depositMethodId;
          }
          if (subscriptionCorpId === corpId && subscriptionMethodId) {
            selectedPaymentMethodId = subscriptionMethodId;
          }
        });
      }
      if (!selectedPaymentMethodId && defaultPaymentMethod?.id) {
        selectedPaymentMethodId = defaultPaymentMethod.id;
      }
      selectedPaymentMethods[corpId] = selectedPaymentMethodId;
    });

    taxRes.forEach((te) => {
      const {
        corpId,
        exempt,
        documentUrl,
        reason,
        number,
      } = te;
      taxExemption[corpId] = {
        exempt,
        documentUrl,
        reason,
        number,
      };
    });

    dispatch('changePartnerAgreementValues', [{
      field: 'allBranchInfo',
      value: companyInfos,
    }, {
      field: 'allPaymentMethods',
      value: paymentMethods,
    }, {
      field: 'allTaxExemptions',
      value: taxExemption,
    }, {
      field: 'selectedPaymentMethods',
      value: selectedPaymentMethods,
    }, {
      field: 'chargebeeCustomers',
      value: chargebeeCustomers,
    }]);
  },
  async newAgreementPaymentMethod({ dispatch, state }) {
    const {
      allBranchInfo,
      selectedCorpId,
      cardToken,
      source,
      paymentMethodId,
      paymentMethod,
      allPaymentMethods,
      selectedPaymentMethods,
    } = state;

    const {
      id,
      firstName,
      lastName,
      email,
      phone,
      billingAddress,
    } = allBranchInfo[selectedCorpId];
    const corporation = {
      id,
      firstName,
      lastName,
      email,
      phone,
      cardToken,
      source,
      paymentMethodId,
      paymentOption: paymentMethod,
      billingAddress,
    };
    const { data } = await apiClient({
      method: 'POST',
      url: '/chargebee/corporation/payment-method/default',
      data: corporation,
    });

    const newPaymentMethods = {
      ...allPaymentMethods,
      [selectedCorpId]: {
        ...allPaymentMethods[selectedCorpId],
        defaultPaymentMethod: data,
        allPaymentMethods: [
          ...allPaymentMethods[selectedCorpId].allPaymentMethods,
          data,
        ],
      },
    };

    const newSelectedMethods = {
      ...selectedPaymentMethods,
      [selectedCorpId]: data.id,
    };

    dispatch('changePartnerAgreementValues', [{
      field: 'allPaymentMethods',
      value: newPaymentMethods,
    }, {
      field: 'showActionModal',
      value: false,
    }, {
      field: 'selectedPaymentMethods',
      value: newSelectedMethods,
    }]);
  },
  async completeAgreementSetup({ state }) {
    const {
      agreement,
      selectedPaymentMethods,
      payee,
      allTaxExemptions,
    } = state;

    const taxExemptReqs = [];
    Object.keys(allTaxExemptions).forEach((corpId) => {
      const {
        exempt,
        reason,
        number,
        expiration,
        tempFile,
      } = allTaxExemptions[corpId];
      if (tempFile) {
        const formData = new FormData();
        formData.append('taxExempt', exempt);
        formData.append('taxDoc', tempFile);
        formData.append('reasonCode', reason);
        formData.append('taxNumber', number);
        formData.append('expireDate', moment(expiration).format('YYYY-MM-DD'));
        formData.append('corpId', corpId);
        taxExemptReqs.push(apiClient({
          method: 'POST',
          url: '/partner/settings/taxexempt',
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }));
      }
    });
    if (taxExemptReqs.length) {
      await Promise.all(taxExemptReqs);
    }

    const msaPaymentMethodId = selectedPaymentMethods[payee.msaPayee];

    const completeAgreement = {
      ...agreement,
      depositMethodId: msaPaymentMethodId,
    };

    const agreementOrders = completeAgreement.agreementOrders.map((order) => {
      const {
        agreementOrderId,
      } = order;
      const depositCorpId = payee[`orderPayee-${agreementOrderId}`];
      const depositMethodId = selectedPaymentMethods[depositCorpId];
      const subscriptionCorpId = payee[`subscriptionPayee-${agreementOrderId}`];
      const subscriptionMethodId = selectedPaymentMethods[subscriptionCorpId];
      return {
        ...order,
        depositCorpId,
        depositMethodId,
        subscriptionCorpId,
        subscriptionMethodId,
      };
    });

    completeAgreement.agreementOrders = agreementOrders;

    await apiClient({
      method: 'PUT',
      url: `/partner/agreement/${agreement.agreementId}/confirm`,
      data: completeAgreement,
    });
  },
  async updateAgreement({ state }) {
    const {
      agreement,
      selectedPaymentMethods,
      payee,
    } = state;
    const msaPaymentMethodId = selectedPaymentMethods[payee.msaPayee];

    const completeAgreement = {
      ...agreement,
      depositMethodId: msaPaymentMethodId,
    };

    const agreementOrders = completeAgreement.agreementOrders.map((order) => {
      const {
        agreementOrderId,
      } = order;
      const depositCorpId = payee[`orderPayee-${agreementOrderId}`];
      const depositMethodId = selectedPaymentMethods[depositCorpId];
      const subscriptionCorpId = payee[`subscriptionPayee-${agreementOrderId}`];
      const subscriptionMethodId = selectedPaymentMethods[subscriptionCorpId];
      return {
        ...order,
        depositCorpId,
        depositMethodId,
        subscriptionCorpId,
        subscriptionMethodId,
      };
    });

    completeAgreement.agreementOrders = agreementOrders;

    await apiClient({
      method: 'PUT',
      url: `/partner/agreement/${agreement.agreementId}`,
      data: completeAgreement,
    });
  },
  async getAgreementPdf(e, agreementId) {
    const { data } = await apiClient({
      method: 'GET',
      url: `/partner/agreement/${agreementId}/pdf`,
    });
    return data;
  },
  resetNewAgreement({ dispatch }) {
    const changes = [{
      field: 'actionModalView',
      value: null,
    }, {
      field: 'showActionModal',
      value: false,
    }, {
      field: 'newAgreement',
      value: { ...initialState().newAgreement },
    }, {
      field: 'agreementPdf',
      value: null,
    }];
    dispatch('changePartnerAgreementValues', changes);
  },
  async createNewAgreementOrder({ state, rootState, dispatch }) {
    const {
      partnerDetail: {
        allBranchAddresses,
      },
      general: {
        products,
        allProducts,
      },
    } = rootState;
    const {
      agreement: {
        manualPayment,
        agreementId,
        contractPeriod,
        deploymentPeriod,
        agreementOrders,
      },
      newAgreement: {
        agreementOrders: nAgreementOrders,
      },
    } = state;
    const [ao] = nAgreementOrders;
    const orderLines = [];
    ao.agreementOrderLines.forEach((ol) => {
      const {
        sku,
        productId,
        version,
      } = ol;
      const product = products.find((p) => p.id === productId);
      const newOl = { ...ol };
      if (version) {
        if (product?.sku.includes('PM')) {
          const hubProduct = allProducts.find((p) => p.sku === 'SMBP1G1A1' && p.price === 0);
          const hubSku = kitSkus.find((s) => {
            if (version === 'v1') {
              return s.value.includes('A1');
            }
            return s.value.includes('A2');
          });
          const hubOl = {
            itemName: hubProduct.itemName,
            price: hubProduct.price,
            productId: hubProduct.id,
            quantity: newOl.quantity,
            total: 0,
            vendor: newOl.vendor,
            sku: hubSku.value,
          };
          newOl.sku = product.sku;
          orderLines.push(newOl);
          orderLines.push(hubOl);
        } else if (product?.sku.includes('CS')) {
          const comfortSku = sensorSkus.find((s) => {
            if (version === 'v1') {
              return s.value.includes('CS') && s.value.includes('A1');
            }
            return s.value.includes('CS') && s.value.includes('A2');
          });
          newOl.sku = comfortSku.value;
          orderLines.push(newOl);
        } else if (product?.sku.includes('FS')) {
          const comfortSku = sensorSkus.find((s) => {
            if (version === 'v1') {
              return s.value.includes('FS') && s.value.includes('A1');
            }
            return s.value.includes('FS') && s.value.includes('A2');
          });
          newOl.sku = comfortSku.value;
          orderLines.push(newOl);
        } else if (product?.sku.includes('WS')) {
          const comfortSku = sensorSkus.find((s) => {
            if (version === 'v1') {
              return s.value.includes('WS') && s.value.includes('A1');
            }
            return s.value.includes('WS') && s.value.includes('A2');
          });
          newOl.sku = comfortSku.value;
          orderLines.push(newOl);
        }
      } else if (!sku) {
        newOl.sku = product?.sku;
        orderLines.push(newOl);
      }
    });
    const address = allBranchAddresses.find((ad) => ad.id === ao.shippingAddressId);

    const depositCorp = agreementOrders.find((o) => o.depositCorpId === address.corpId);
    const subCorp = agreementOrders.find((o) => o.subscriptionCorpId === address.corpId);
    const newOrder = {
      ...ao,
      agreementId,
      orderStatus: manualPayment ? 'Open' : 'Payment Pending',
      contractTerm: contractPeriod,
      deploymentPeriod,
      corpId: address.corpId,
      depositCorpId: address.corpId,
      depositMethodId: depositCorp?.depositMethodId,
      subscriptionCorpId: address.corpId,
      subscriptionMethodId: subCorp?.subscriptionMethodId,
      agreementOrderLines: orderLines,
    };
    await apiClient({
      method: 'POST',
      url: '/partner/agreement/order/new',
      data: newOrder,
    });

    await dispatch('getPartnerAgreement', agreementId);
    dispatch('changePartnerAgreementValues', [{
      field: 'showActionModal',
      value: false,
    }]);
  },
  async createAgreementChargebeeContact({ dispatch, state }, payload) {
    const {
      chargebeeCustomers,
    } = state;
    const {
      invoiceEmail,
      corpId,
    } = payload;
    const { data } = await apiClient({
      method: 'POST',
      url: `/chargebee/corp/${corpId}/contact/add/${invoiceEmail}`,
    });
    const newChargebeeCustomers = {
      ...chargebeeCustomers,
      [corpId]: data,
    };
    dispatch('changePartnerAgreementValues', [{
      field: 'chargebeeCustomers',
      value: newChargebeeCustomers,
    }]);
  },
  async deleteAgreementChargebeeContact({ dispatch, state }, payload) {
    const {
      chargebeeCustomers,
    } = state;
    const {
      corpId,
      contactId,
    } = payload;
    const { data } = await apiClient({
      method: 'DELETE',
      url: `/chargebee/corp/${corpId}/contact/delete/${contactId}`,
    });
    const newChargebeeCustomers = {
      ...chargebeeCustomers,
      [corpId]: data,
    };
    dispatch('changePartnerAgreementValues', [{
      field: 'chargebeeCustomers',
      value: newChargebeeCustomers,
    }]);
  },
  async getAgreementOrderTax({ dispatch, state, rootState }) {
    const {
      agreement: {
        agreementOrders,
      },
      agreement,
      allBranchInfo,
    } = state;

    const {
      general: { allProducts },
    } = rootState;

    const oneTimeTaxReq = [];
    const subTaxReq = [];

    agreementOrders.forEach((o) => {
      const orderLines = [];
      o.agreementOrderLines.forEach((ol) => {
        const product = allProducts.find((p) => p.id === ol.productId);
        const pol = {
          lineNumber: ol.productId,
          itemName: ol.itemName,
          sku: ol.sku,
          price: ol.price,
          type: product.type,
          quantity: ol.quantity,
          total: ol.total,
        };
        orderLines.push(pol);
      });
      if (o.shippingCost) {
        const shipping = allProducts.find((p) => p.sku === 'SHIPPING');
        const pol = {
          lineNumber: shipping.id,
          itemName: shipping.itemName,
          sku: shipping.sku,
          price: o.shippingCost,
          type: shipping.type,
          quantity: 1,
          total: o.shippingCost,
        };
        orderLines.push(pol);
      }
      const hardwareOrderLine = orderLines.filter((ol) => ol.type.toLowerCase() !== 'subscription' && ol.type.toLowerCase() !== 'monitoring');
      const subscriptionOrderLine = orderLines.filter(
        (ol) => ol.type.toLowerCase() === 'subscription' || ol.type.toLowerCase() === 'monitoring',
      );
      const [bAddress] = allBranchInfo[o.corpId].billingAddress;
      if (hardwareOrderLine.length > 0) {
        oneTimeTaxReq.push(apiClient({
          method: 'POST',
          url: '/chargebee/corp/estimate/invoice',
          data: {
            billingAddress: bAddress,
            orderLine: hardwareOrderLine,
          },
        }));
      }
      if (subscriptionOrderLine.length > 0) {
        subTaxReq.push(apiClient({
          method: 'POST',
          url: '/chargebee/corp/estimate/subscription',
          data: {
            billingAddress: bAddress,
            orderLine: subscriptionOrderLine,
          },
        }));
      }
    });

    const oneTimeTaxRes = await Promise.all(oneTimeTaxReq);
    const subTaxRes = await Promise.all(subTaxReq);

    const newAgreementOrders = agreementOrders.map((o, idx) => {
      const order = { ...o };
      if (oneTimeTaxRes[idx]) {
        const { data } = oneTimeTaxRes[idx];
        if (data && data.taxes.length > 0) {
          const oneTimeTax = data.taxes.reduce((acc, item) => acc + item.amount, 0);
          order.oneTimeTax = oneTimeTax / 100;
        }
      }
      if (subTaxRes[idx]) {
        const { data: subD } = subTaxRes[idx];
        if (subD && subD.taxes.length > 0) {
          const subTax = subD.taxes.reduce((acc, item) => acc + item.amount, 0);
          order.subscriptionTax = subTax / 100;
        }
      }
      return order;
    });

    const newAgreement = {
      ...agreement,
      agreementOrders: newAgreementOrders,
    };

    dispatch('changePartnerAgreementValues', [{
      field: 'agreement',
      value: newAgreement,
    }]);
  },
};

export default actions;
